/**
 * KioskIdleCarousel - Fullscreen promotional carousel shown when kiosk is idle
 * Rotates through images/videos, touch anywhere to dismiss
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, Hand } from 'lucide-react';
import { BULACCINO_LOGO } from './KioskContext';

// Default promotional slides
const DEFAULT_SLIDES = [
  {
    id: 1,
    type: 'image',
    url: 'https://images.pexels.com/photos/29517581/pexels-photo-29517581.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Freshly Roasted Coffee',
    subtitle: 'Start your day with the perfect cup',
    overlay: 'dark'
  },
  {
    id: 2,
    type: 'image',
    url: 'https://images.pexels.com/photos/19279845/pexels-photo-19279845.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Delicious Breakfast',
    subtitle: 'Made fresh every morning',
    overlay: 'dark'
  },
  {
    id: 3,
    type: 'image',
    url: 'https://images.pexels.com/photos/16096610/pexels-photo-16096610.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Refreshing Smoothies',
    subtitle: 'Beat the tropical heat',
    overlay: 'dark'
  },
  {
    id: 4,
    type: 'image',
    url: 'https://images.unsplash.com/photo-1759912256095-72174d9e88d7?w=1920',
    title: 'Sweet Treats',
    subtitle: 'Indulge yourself today',
    overlay: 'dark'
  }
];

export default function KioskIdleCarousel({ 
  show, 
  onDismiss, 
  slides = DEFAULT_SLIDES,
  rotationInterval = 5000 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-rotate slides
  useEffect(() => {
    if (!show || slides.length <= 1) return;

    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 300);
    }, rotationInterval);

    return () => clearInterval(timer);
  }, [show, slides.length, rotationInterval]);

  // Reset to first slide when shown
  useEffect(() => {
    if (show) {
      setCurrentIndex(0);
    }
  }, [show]);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    onDismiss?.();
  }, [onDismiss]);

  const goToSlide = (index) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  const goNext = (e) => {
    e?.stopPropagation();
    goToSlide((currentIndex + 1) % slides.length);
  };

  const goPrev = (e) => {
    e?.stopPropagation();
    goToSlide((currentIndex - 1 + slides.length) % slides.length);
  };

  if (!show) return null;

  const currentSlide = slides[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black cursor-pointer"
      onClick={handleDismiss}
      data-testid="kiosk-idle-carousel"
    >
      {/* Background Image/Video */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {currentSlide.type === 'video' ? (
          <video
            src={currentSlide.url}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={currentSlide.url}
            alt={currentSlide.title || 'Promotional slide'}
            className="w-full h-full object-cover"
          />
        )}
        
        {/* Overlay */}
        <div className={`absolute inset-0 ${
          currentSlide.overlay === 'dark' 
            ? 'bg-gradient-to-t from-black/80 via-black/30 to-transparent' 
            : 'bg-gradient-to-t from-white/80 via-white/30 to-transparent'
        }`} />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-12">
        {/* Top - Logo */}
        <div className="flex justify-center">
          <div className="bg-white rounded-3xl p-6 shadow-2xl">
            <img 
              src={BULACCINO_LOGO}
              alt="Bulaccino Cafe"
              className="w-32 h-32 object-contain"
            />
          </div>
        </div>

        {/* Middle - Slide Content */}
        <div className={`text-center ${isTransitioning ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
          {currentSlide.title && (
            <h1 className="text-7xl font-bold text-white mb-6 drop-shadow-lg">
              {currentSlide.title}
            </h1>
          )}
          {currentSlide.subtitle && (
            <p className="text-4xl text-white/90 drop-shadow-lg">
              {currentSlide.subtitle}
            </p>
          )}
        </div>

        {/* Bottom - Touch to Order */}
        <div className="flex flex-col items-center">
          {/* Slide Indicators */}
          {slides.length > 1 && (
            <div className="flex gap-3 mb-8">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(idx);
                  }}
                  className={`w-4 h-4 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-white w-8' 
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Touch Prompt */}
          <div className="bg-white/20 backdrop-blur-md rounded-3xl px-16 py-8 border border-white/30 animate-pulse">
            <div className="flex items-center gap-4 text-white">
              <Hand className="w-12 h-12" />
              <span className="text-4xl font-bold">Touch to Order</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-8 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center transition-all"
          >
            <ChevronLeft className="w-10 h-10 text-white" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-8 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center transition-all"
          >
            <ChevronRight className="w-10 h-10 text-white" />
          </button>
        </>
      )}
    </div>
  );
}
